import { useMemo } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'quartz-routing/react-router';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Button } from 'foundations-components/transitional/components/Button';
import { HR } from 'foundations-components/transitional/components/HR';
import { Text } from 'foundations-components/components/Text';
import { FloatingForm } from 'foundations-components/transitional/components/FloatingForm';
import { getIsIframePreviewUrl } from '../../utils/urls';
import { OPEN } from '../../constants/Statuses';
import { useSubtotals } from '../../context/SubtotalsProvider';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import DiscountCodeSubtotal from './DiscountCodeSubtotal';
import ApplyDiscountCodeButton from './ApplyDiscountCodeButton';
import DiscountCodeApplied from './DiscountCodeApplied';
import DiscountAmount from './DiscountAmount';
import { useTransactionLimits } from '../../hooks/useTransactionLimits';
import { getDiscountAmount, getDiscountCodeErrorMessage, getIsAppliedTextVisible, getIsDiscountInputEnabled, getIsDiscountPending, setIsDiscountInputEnabled } from '../../state/DiscountCode/discountCodeSlice';
import RemoveDiscountCodeButton from './RemoveDiscountCodeButton';
import DiscountCodeInput from './DiscountCodeInput';
import useDiscountCodeError from '../../hooks/DiscountCode/useDiscountCodeError';
import useReapplyDiscountCode from '../../hooks/DiscountCode/useReapplyDiscountCode';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const DISCOUNT_LEFT_GRID = 4;
export const DISCOUNT_RIGHT_GRID = 8;
const DiscountCode = ({
  checkoutSession,
  checkoutFees
}) => {
  const reduxDispatch = useDispatch();
  const {
    pathname
  } = useLocation();
  const isIframePreviewUrl = getIsIframePreviewUrl(pathname);
  const {
    formState
  } = usePaymentFormContext();
  const {
    limits
  } = useTransactionLimits();
  const {
    currencyCode,
    status
  } = checkoutSession;
  const discountCodeError = formState.get('discountCodeError');
  const isDiscountInputEnabled = useSelector(getIsDiscountInputEnabled);
  const isDiscountPending = useSelector(getIsDiscountPending);
  const isCheckoutSubmitPending = formState.get('isCheckoutSubmitPending');
  const isPending = isDiscountPending || isCheckoutSubmitPending;
  const isAppliedTextVisible = useSelector(getIsAppliedTextVisible);
  const discountCodeErrorMessage = useSelector(getDiscountCodeErrorMessage);
  const discountAmount = useSelector(getDiscountAmount);
  const isDiscountCodeApplied = formState.get('isDiscountCodeApplied');
  const paymentMethod = formState.get('paymentMethod');
  const isApplyButtonVisible = !isDiscountCodeApplied;
  const minimumAmount = limits && limits.minimumAmountForPaymentMethod[paymentMethod];

  // calculated subtotal before discount
  const {
    firstPaymentSubtotal
  } = useSubtotals();
  useDiscountCodeError({
    discountCodeError
  });
  useReapplyDiscountCode({
    isDiscountCodeApplied,
    checkoutSession,
    isIframePreviewUrl,
    minimumAmount,
    paymentMethod,
    checkoutFees
  });
  const discountCode = formState.get('discountCode');
  const client = useDataFetchingClient();
  const applyOrDiscountAmount = useMemo(() => {
    if (!discountCode || !!discountCodeErrorMessage) {
      return null;
    }
    if (isApplyButtonVisible) {
      return /*#__PURE__*/_jsx(ApplyDiscountCodeButton, {
        checkoutSession: checkoutSession,
        checkoutFees: checkoutFees,
        minimumAmount: minimumAmount,
        isPending: isPending,
        paymentMethod: paymentMethod
      });
    }
    if (isAppliedTextVisible) {
      return /*#__PURE__*/_jsx(DiscountCodeApplied, {});
    }
    return /*#__PURE__*/_jsx(DiscountAmount, {
      currencyCode: currencyCode
    });
  }, [checkoutSession, currencyCode, discountCode, discountCodeErrorMessage, isAppliedTextVisible, isApplyButtonVisible, minimumAmount, isPending, checkoutFees, paymentMethod]);
  const discountSection = /*#__PURE__*/_jsx(FloatingForm, {
    marginBlockEnd: "600",
    paddingBlockEnd: "25",
    children: /*#__PURE__*/_jsxs(Flex, {
      paddingInline: "600",
      flexDirection: "column",
      alignItems: "stretch",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        flexGrow: "1",
        children: [/*#__PURE__*/_jsx(DiscountCodeInput, {
          status: status
        }), applyOrDiscountAmount]
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        justifyContent: "space-between",
        children: [status === OPEN && /*#__PURE__*/_jsx(Flex, {
          alignSelf: "start",
          children: /*#__PURE__*/_jsx(RemoveDiscountCodeButton, {
            checkoutSession: checkoutSession,
            isIframePreviewUrl: isIframePreviewUrl,
            client: client,
            minimumAmount: minimumAmount,
            paymentMethod: paymentMethod,
            checkoutFees: checkoutFees
          })
        }), !discountCodeErrorMessage && !!discountAmount.discountedPercentage && isDiscountCodeApplied && !isAppliedTextVisible && !isApplyButtonVisible && /*#__PURE__*/_jsx(Text, {
          textAlign: "right",
          color: "fill-positive-default",
          typography: "heading-25",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.afterDiscount",
            options: {
              discountValue: `${discountAmount.discountedPercentage}%`
            }
          })
        })]
      })]
    })
  });
  const showDiscountSection = isDiscountInputEnabled || status === OPEN;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(DiscountCodeSubtotal, {
      subtotal: firstPaymentSubtotal,
      currencyCode: currencyCode
    }), /*#__PURE__*/_jsx(HR, {
      distance: "medium"
    }), showDiscountSection && /*#__PURE__*/_jsxs(_Fragment, {
      children: [isDiscountInputEnabled ? discountSection : /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        paddingInline: "600",
        "data-test-id": "add-discount-code-button",
        onClick: () => {
          reduxDispatch(setIsDiscountInputEnabled(true));
        },
        responsive: false,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.plusDiscountCode"
        })
      }), /*#__PURE__*/_jsx(HR, {
        distance: "medium"
      })]
    })]
  });
};
export default DiscountCode;